import React, { useEffect, useState } from "react";
import {
  Button,
  Box,
  Grid,
  FormControl,
  MenuItem,
  InputLabel,
  Select,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import {
  AnnouncementType,
  CURRENT_PAGE,
  LIMIT_PAGE,
  Platform,
  TOTAL_PAGE,
} from "@app/constants";
import { useTranslation } from "react-i18next";
import { clearMachineReportsPreferences } from "@app/helpers";
import AnnouncementsTable from "./Components/AnnouncementsTable";
import { getUser } from "@app/api";
import { Helmet, PageTitle } from "@app/components/atoms";
import { IconPlus } from "@app/components/icons";
import { InputText } from "primereact/inputtext";
import CreateAnnouncementDialog from "./Components/CreateAnnouncementDialog";
import {
  AnnouncementGlobal,
  AnnouncementPlatformFilter,
  AnnouncementsRequestDef,
  AnnouncementTypeFilter,
  PaginationData,
} from "@app/types";
import { apiGetGlobalAnnouncements } from "@app/api/announcements/announcements.api";

const typeFilter: AnnouncementTypeFilter[] = [
  {
    label: "all",
    key: "all",
    announcementType: "all",
  },
  {
    label: "announcementTypeValues.downtime",
    key: "downtime",
    announcementType: AnnouncementType.DOWNTIME,
  },
  {
    label: "formAction.update",
    key: "update",
    announcementType: AnnouncementType.UPDATE,
  },
  {
    label: "announcementTypeValues.feature",
    announcementType: AnnouncementType.FEATURE,
    key: "feature",
  },
  {
    label: "announcementTypeValues.other",
    announcementType: AnnouncementType.OTHER,
    key: "other",
  },
];

const platformFilter: AnnouncementPlatformFilter[] = [
  {
    label: "BOTH",
    key: "both",
    announcementPlatform: "BOTH",
  },
  {
    label: "Platform.WEBAPP",
    key: "webapp",
    announcementPlatform: Platform.WEBAPP,
  },
  {
    label: "Platform.MOBILE_APP",
    key: "mobile_app",
    announcementPlatform: Platform.MOBILE_APP,
  },
];

export function CreateAnnouncement() {
  const { t } = useTranslation();
  const [userTimeFormat, setUserTimeFormat] = useState<string>("");
  const [refreshAnnouncements, setRefreshAnnouncements] =
    useState<boolean>(false);
  const [pageInfo, setPageInfo] = useState<PaginationData>({
    total: TOTAL_PAGE,
    page: CURRENT_PAGE,
    limit: LIMIT_PAGE,
  });
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [searchTextClear, setSearchTextClear] = useState("");
  const [searchTextAnnouncement, setSearchTextAnnouncement] = useState("");
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [typeFilterList] = useState(typeFilter);
  const [announcementTypeFilter, setAnnouncementTypeFilter] = useState<
    AnnouncementType | "all"
  >("all");
  const [platformFilterList] = useState(platformFilter);
  const [announcementPlatformFilter, setAnnouncementPlatformFilter] = useState<
    Platform | "BOTH"
  >("BOTH");
  const [announcements, setAnnouncements] = useState<AnnouncementGlobal[]>();
  const [announcementsLoading, setAnnouncementsLoading] =
    useState<boolean>(true);
  const [field, setField] = useState<string>("");
  const [order, setOrder] = useState<string | undefined>();

  useEffect(() => {
    clearMachineReportsPreferences();
  }, []);

  const fetchUserData = async () => {
    try {
      const getUserAPI = await getUser();
      if (!getUserAPI || !getUserAPI?.data?.data) {
        throw new Error("Failed to fetch user data");
      }
      let timeFormat = getUserAPI?.data?.data?.timeFormat || "yyyy/MM/dd";

      if (!timeFormat.includes("HH") && !timeFormat.includes("mm")) {
        timeFormat += " HH:mm";
      }

      setUserTimeFormat(timeFormat);
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  };

  useEffect(() => {
    fetchUserData();
  }, []);

  const handleClickTypeFilter = (
    _: React.MouseEvent<HTMLElement> | null,
    announcementType: AnnouncementType | "all",
  ) => {
    setPageInfo({
      ...pageInfo,
      page: CURRENT_PAGE,
    });
    setAnnouncementTypeFilter(announcementType);
  };

  const handleClickPlatformFilter = (
    _: React.MouseEvent<HTMLElement> | null,
    announcementPlatform: Platform | "BOTH",
  ) => {
    setPageInfo({
      ...pageInfo,
      page: CURRENT_PAGE,
    });
    setAnnouncementPlatformFilter(announcementPlatform);
  };

  const clearSearch = () => {
    setSearchTextClear("");

    setSearchTextAnnouncement("");
    setPageInfo((prev) => ({ ...prev, page: 1 }));
  };

  const handleSearch = () => {
    setPageInfo((prev) => ({
      ...prev,
      limit: LIMIT_PAGE,
      page: 1,
    }));
    setSearchTextAnnouncement(searchTextClear);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && searchTextClear.trim()) {
      handleSearch();
    }
  };

  const getAnnouncements = async (param: AnnouncementsRequestDef) => {
    setAnnouncementsLoading(true);
    try {
      const db = await apiGetGlobalAnnouncements(param);
      const data: AnnouncementGlobal[] = db.data.data;
      setPageInfo(db.data.meta.pageInfo);
      setAnnouncements(
        data.map((value) => ({ ...value, id: value.announcementId })),
      );
    } catch (error) {
      console.error("Failed to fetch announcements:", error);
    } finally {
      setAnnouncementsLoading(false);
    }
  };

  useEffect(() => {
    const params = {
      limit: LIMIT_PAGE,
      page: pageInfo.page,
    };
    getAnnouncements({
      ...(field && order
        ? {
            ...params,
            orderBys: [{ field, order }],
            ...(searchTextAnnouncement
              ? { searchText: searchTextAnnouncement }
              : {}),
            ...(announcementPlatformFilter &&
            announcementPlatformFilter !== "BOTH"
              ? { platform: announcementPlatformFilter }
              : {}),
            ...(announcementTypeFilter && announcementTypeFilter !== "all"
              ? { announcementType: announcementTypeFilter }
              : {}),
          }
        : {
            ...params,
            ...(searchTextAnnouncement
              ? { searchText: searchTextAnnouncement }
              : {}),
            ...(announcementPlatformFilter &&
            announcementPlatformFilter !== "BOTH"
              ? { platform: announcementPlatformFilter }
              : {}),
            ...(announcementTypeFilter && announcementTypeFilter !== "all"
              ? { announcementType: announcementTypeFilter }
              : {}),
          }),
    });
    if (refreshAnnouncements) setRefreshAnnouncements(false);
  }, [
    refreshAnnouncements,
    order,
    pageInfo.page,
    searchTextAnnouncement,
    field,
    announcementTypeFilter,
    announcementPlatformFilter,
  ]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Helmet title={t("metaInfo.createAnnouncements")} />
      <CreateAnnouncementDialog
        createDialogOpen={createDialogOpen}
        setCreateDialogOpen={setCreateDialogOpen}
        setRefreshAnnouncements={setRefreshAnnouncements}
        timeFormat={userTimeFormat}
      />
      <Box
        className="container"
        sx={{ mx: { xs: 2, sm: 3, md: 5, xl: "auto" }, px: "24px" }}
      >
        <Box sx={{ py: 4, margin: "auto" }}>
          <Grid container>
            <Grid item xs={12} sm={12} md={12} lg={12}>
              <PageTitle
                title="announcements"
                className="mb-25 sp-p24"
              ></PageTitle>
            </Grid>
            <Grid item xs={12} sm={6} md={6} lg={6} className="mt-24">
              <div
                className="input-field"
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "15px",
                  paddingRight: "0px",
                  position: "relative",
                  flexWrap: "wrap",
                  gap: 20,
                }}
              >
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel id="announcement-type-label">
                    {t("subType")}
                  </InputLabel>
                  <Select
                    labelId="announcement-type-label"
                    value={announcementTypeFilter}
                    label={t("subType")}
                    displayEmpty
                    onChange={(e) => {
                      handleClickTypeFilter(
                        null,
                        e.target.value as AnnouncementType | "all",
                      );
                    }}
                    renderValue={(selected: AnnouncementType | "all") => {
                      switch (selected) {
                        case AnnouncementType.DOWNTIME:
                          return t("announcementTypeValues.DOWNTIME");
                        case AnnouncementType.UPDATE:
                          return t("formAction.update");
                        case AnnouncementType.FEATURE:
                          return t("announcementTypeValues.FEATURE");
                        case AnnouncementType.OTHER:
                          return t("announcementTypeValues.OTHER");
                        default:
                          return t("all");
                      }
                    }}
                  >
                    {typeFilterList.map((tab, index) => (
                      <MenuItem
                        key={index}
                        value={tab.announcementType}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        {(() => {
                          switch (tab.announcementType) {
                            case AnnouncementType.DOWNTIME:
                              return t("announcementTypeValues.DOWNTIME");
                            case AnnouncementType.UPDATE:
                              return t("formAction.update");
                            case AnnouncementType.FEATURE:
                              return t("announcementTypeValues.FEATURE");
                            case AnnouncementType.OTHER:
                              return t("announcementTypeValues.OTHER");
                            default:
                              return t("all");
                          }
                        })()}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel id="announcement-platform-label">
                    {t("announcementTable.platform")}
                  </InputLabel>
                  <Select
                    labelId="announcement-platform-label"
                    value={announcementPlatformFilter}
                    label={t("announcementTable.platform")}
                    displayEmpty
                    onChange={(e) => {
                      handleClickPlatformFilter(
                        null,
                        e.target.value as Platform | "BOTH",
                      );
                    }}
                    renderValue={(selected: Platform | "BOTH") => {
                      switch (selected) {
                        case Platform.WEBAPP:
                          return t("platformValues.WEBAPP");
                        case Platform.MOBILE_APP:
                          return t("platformValues.MOBILE_APP");
                        case "BOTH":
                          return t("platformValues.BOTH");
                        default:
                          return t("all");
                      }
                    }}
                  >
                    {platformFilterList.map((tab, index) => (
                      <MenuItem
                        key={index}
                        value={tab.announcementPlatform}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        {typeof tab.label !== "string" && tab.label}
                        {(() => {
                          switch (tab.announcementPlatform) {
                            case Platform.WEBAPP:
                              return t("platformValues.WEBAPP");
                            case Platform.MOBILE_APP:
                              return t("platformValues.MOBILE_APP");
                            case "BOTH":
                              return t("platformValues.BOTH");
                            default:
                              return t("all");
                          }
                        })()}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
              md={6}
              lg={6}
              className={!isSmallScreen ? "mt-24" : ""}
            >
              <div
                className="input-field"
                style={{
                  display: "flex",
                  justifyContent: isSmallScreen ? "flex-start" : "flex-end",
                  alignItems: "center",
                  marginBottom: "15px",
                  marginTop: "-5px",
                  paddingRight: "0px",
                  position: "relative",
                }}
              >
                <Button
                  variant="contained"
                  disableElevation
                  className="btn-add-announcement"
                  onClick={() => setCreateDialogOpen(true)}
                >
                  <IconPlus />
                  <span style={{ marginLeft: "8px" }}>
                    {t("createAnnouncements")}
                  </span>
                </Button>
              </div>
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={12}>
              <div
                className="input-field"
                style={{
                  display: "flex",
                  justifyContent: isSmallScreen ? "flex-start" : "flex-end",
                  alignItems: "center",
                  marginBottom: "15px",

                  paddingRight: "0px",
                  position: "relative",
                }}
              >
                <div
                  style={{
                    position: "relative",
                    width: "100%",
                    maxWidth: "277px",
                  }}
                >
                  <InputText
                    type="text"
                    value={searchTextClear}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const value = e.target.value;

                      setSearchTextClear(value);
                      if (value.trim() === "") {
                        setSearchTextAnnouncement("");
                        setPageInfo((prev) => ({ ...prev, page: 1 }));
                      }
                    }}
                    onKeyDown={handleKeyDown}
                    placeholder={t("Customermachinenumber")}
                    className="custom-input"
                    style={{
                      width: "100%",
                      paddingLeft: "1.0rem",
                      paddingRight: "2rem",
                      height: "50px",
                      border: "1.5px solid #bcbcbc",
                      borderRadius: "30px",
                    }}
                  />
                  {searchTextClear && (
                    <button
                      className="clear-button"
                      onClick={clearSearch}
                      aria-label="Clear search"
                      type="button"
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>
            </Grid>
          </Grid>
          {userTimeFormat && (
            <AnnouncementsTable
              timeFormat={userTimeFormat}
              announcements={announcements}
              announcementsLoading={announcementsLoading}
              field={field}
              setField={setField}
              order={order}
              setOrder={setOrder}
              getAnnouncements={getAnnouncements}
              pageInfo={pageInfo}
              setPageInfo={setPageInfo}
            />
          )}
        </Box>
      </Box>
    </LocalizationProvider>
  );
}
